import { Anchor, Button, Stack, Text } from '@mantine/core';
import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { AuthDivider } from '@/auth/components/AuthDivider';
import defaultNotifications from '@/components/functions/defaultNotifications';

import type { BiometricEnrollment } from './biometricEnrollment';

import { biometricMethodName, disableBiometrics, signInWithBiometrics } from './biometricAuth';
import { getBiometricSupport } from './biometricDevice';
import { getEnrollment } from './biometricEnrollment';
import { BiometricIcon } from './BiometricIcon';

type BiometricSignInButtonProps = {
	/** Prompt as soon as the button appears — only when a protected route sent the user here. */
	autoPrompt: boolean;
	disabled?: boolean;
	onSignedIn: () => void;
	/** The device cannot offer biometric sign-in (anymore); the parent should stop reserving space. */
	onHide: () => void;
};

export function BiometricSignInButton({ autoPrompt, disabled, onSignedIn, onHide }: BiometricSignInButtonProps) {
	const [enrollment] = useState<BiometricEnrollment | null>(getEnrollment);
	const [isAvailable, setIsAvailable] = useState(false);
	const [isSigningIn, setIsSigningIn] = useState(false);

	const signIn = async () => {
		setIsSigningIn(true);
		const result = await signInWithBiometrics();
		if (result === 'busy') {
			return;
		}
		setIsSigningIn(false);

		if (result === 'signed-in') {
			onSignedIn();
		} else if (result === 'revoked') {
			onHide();
			defaultNotifications.showError({ message: t('biometric.revoked') });
		} else if (result === 'failed') {
			defaultNotifications.showError({ message: t('biometric.signInFailed') });
		}
	};

	useEffect(() => {
		if (!enrollment) {
			onHide();
			return;
		}
		void getBiometricSupport().then(({ available }) => {
			if (!available) {
				onHide();
				return;
			}
			setIsAvailable(true);
			if (autoPrompt) {
				void signIn();
			}
		});
		// Runs once on mount: re-prompting on every render would trap the user in the sheet.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!enrollment || !isAvailable) {
		return null;
	}

	const forgetThisDevice = () => {
		onHide();
		void disableBiometrics();
	};

	return (
		<Stack gap="xs" align="stretch">
			<Button
				size="md"
				leftSection={<BiometricIcon kind={enrollment.kind} size={22} />}
				loading={isSigningIn}
				disabled={disabled}
				onClick={() => void signIn()}
			>
				{t('biometric.signInWith', { method: biometricMethodName(enrollment.kind) })}
			</Button>
			{enrollment.email && (
				<Text size="sm" c="dimmed" ta="center">
					{enrollment.email}
				</Text>
			)}
			<Anchor component="button" type="button" size="sm" c="dimmed" onClick={forgetThisDevice}>
				{t('biometric.useAnotherAccount')}
			</Anchor>
			<AuthDivider label={t('login.or')} />
		</Stack>
	);
}
